// VariableManager.jsx - Dashboard template variable CRUD modal
import React, { useState, useEffect } from 'react';
import { Plus, X, Play, Trash2, Variable } from 'lucide-react';
import { DEFAULT_VARIABLE, VARIABLE_TYPES } from './constants';
import questdbService from '../../services/questdb';
import mysqlService from '../../services/mysql';

const NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

function generateId() {
  return `var_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

export default function VariableManager({ variables, onSave, onClose, darkMode, plantId, availableTables }) {
  const [localVars, setLocalVars] = useState(() =>
    (variables || []).map(v => ({ ...DEFAULT_VARIABLE, ...v }))
  );
  const [selectedId, setSelectedId] = useState(localVars[0]?.id || null);
  const [editingColumns, setEditingColumns] = useState([]);
  const [previewOptions, setPreviewOptions] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const theme = darkMode ? {
    bg: '#0f172a',
    card: '#1e293b',
    cardHover: '#252f40',
    text: '#f1f5f9',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    border: '#334155',
    accent: '#6366f1',
    danger: '#ef4444',
    success: '#10b981'
  } : {
    bg: '#f9fafb',
    card: '#ffffff',
    cardHover: '#f3f4f6',
    text: '#111827',
    textSecondary: '#374151',
    textMuted: '#6b7280',
    border: '#e5e7eb',
    accent: '#6366f1',
    danger: '#ef4444',
    success: '#10b981'
  };

  const selected = localVars.find(v => v.id === selectedId) || null;

  // For MySQL variables: list of databases and tables fetched on demand
  const [mysqlDatabases, setMysqlDatabases] = useState([]);
  const [mysqlTables, setMysqlTables] = useState([]);

  // Normalize whatever shape the MySQL service returns (objects with the
  // first column, or already-strings) to a plain string array.
  const toStringList = (raw) => {
    if (!Array.isArray(raw)) return [];
    return raw.map(row => {
      if (row == null) return '';
      if (typeof row === 'string') return row;
      // Object form: take the first non-empty value
      const vals = Object.values(row);
      return vals.length ? String(vals[0]) : '';
    }).filter(Boolean);
  };

  // Load MySQL databases when a query variable is selected with engine=mysql
  useEffect(() => {
    if (selected?.type === 'query' && selected?.dbEngine === 'mysql') {
      mysqlService.getDatabases()
        .then(dbs => setMysqlDatabases(toStringList(dbs)))
        .catch(err => { console.warn('[VariableManager] getDatabases failed:', err?.message); setMysqlDatabases([]); });
    }
  }, [selected?.type, selected?.dbEngine]);

  // Load MySQL tables when a database is picked
  useEffect(() => {
    if (selected?.type === 'query' && selected?.dbEngine === 'mysql' && selected?.mysqlDatabase) {
      mysqlService.getTables(selected.mysqlDatabase)
        .then(tbls => setMysqlTables(toStringList(tbls)))
        .catch(err => { console.warn('[VariableManager] getTables failed:', err?.message); setMysqlTables([]); });
    } else {
      setMysqlTables([]);
    }
  }, [selected?.dbEngine, selected?.mysqlDatabase, selected?.type]);

  // Fetch columns when selected variable's table changes (works for both engines)
  useEffect(() => {
    if (!selected || selected.type !== 'query' || !selected.table) {
      setEditingColumns([]);
      return;
    }
    const isMySQL = selected.dbEngine === 'mysql';
    if (isMySQL && !selected.mysqlDatabase) {
      setEditingColumns([]);
      return;
    }
    const sql = isMySQL
      ? `SELECT * FROM \`${selected.table}\` LIMIT 1`
      : `SELECT * FROM ${selected.table} LIMIT 1`;
    const fetcher = isMySQL
      ? mysqlService.query(sql, selected.mysqlDatabase)
      : questdbService.query(sql, plantId);
    fetcher
      .then(result => {
        if (Array.isArray(result) && result.length > 0 && result[0] && typeof result[0] === 'object') {
          setEditingColumns(Object.keys(result[0]));
        } else {
          setEditingColumns([]);
        }
      })
      .catch(err => { console.warn('[VariableManager] column fetch failed:', err?.message); setEditingColumns([]); });
  }, [selected?.table, selected?.type, selected?.dbEngine, selected?.mysqlDatabase, plantId]);

  const updateSelected = (patch) => {
    setLocalVars(prev =>
      prev.map(v => v.id === selectedId ? { ...v, ...patch } : v)
    );
    if (patch.name !== undefined) {
      setNameError(
        patch.name && !NAME_REGEX.test(patch.name)
          ? 'Name must start with a letter/underscore and contain only letters, digits, underscores'
          : ''
      );
    }
  };

  const handleAddVariable = () => {
    const newVar = { ...DEFAULT_VARIABLE, id: generateId() };
    setLocalVars(prev => [...prev, newVar]);
    setSelectedId(newVar.id);
    setPreviewOptions([]);
    setNameError('');
  };

  const handleDeleteVariable = (id) => {
    setLocalVars(prev => {
      const remaining = prev.filter(v => v.id !== id);
      if (selectedId === id) {
        setSelectedId(remaining[0]?.id || null);
      }
      return remaining;
    });
  };

  const handlePreview = async () => {
    if (!selected) return;
    setPreviewLoading(true);
    setPreviewOptions([]);
    try {
      if (selected.type === 'query' && selected.table && selected.column) {
        const isMySQL = selected.dbEngine === 'mysql';
        const sql = isMySQL
          ? `SELECT DISTINCT \`${selected.column}\` FROM \`${selected.table}\` ORDER BY \`${selected.column}\` LIMIT 100`
          : `SELECT DISTINCT ${selected.column} FROM ${selected.table} ORDER BY ${selected.column} LIMIT 100`;
        const result = isMySQL
          ? await mysqlService.query(sql, selected.mysqlDatabase)
          : await questdbService.query(sql, plantId);
        const opts = result.map(row => String(row[selected.column])).filter(Boolean);
        setPreviewOptions(opts);
      } else if (selected.type === 'custom') {
        const opts = (selected.values || []).filter(Boolean);
        setPreviewOptions(opts);
      }
    } catch {
      setPreviewOptions([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = () => {
    // Validate all variables
    for (const v of localVars) {
      if (!v.name) {
        alert(`Variable missing a name. Please fill in all variable names before saving.`);
        setSelectedId(v.id);
        return;
      }
      if (!NAME_REGEX.test(v.name)) {
        alert(`Variable name "${v.name}" is invalid. Use only letters, digits, and underscores, starting with a letter or underscore.`);
        setSelectedId(v.id);
        return;
      }
    }
    onSave(localVars);
  };

  const inputStyle = {
    width: '100%',
    padding: '7px 10px',
    background: darkMode ? '#0f172a' : '#f9fafb',
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    color: theme.text,
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    colorScheme: darkMode ? 'dark' : 'light'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    color: theme.textMuted,
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const sectionStyle = {
    marginBottom: '16px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.75)',
      backdropFilter: 'blur(8px)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: theme.card,
        borderRadius: '12px',
        width: '780px',
        maxWidth: '95vw',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.border}`,
        boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Variable size={18} color={theme.accent} />
            <div>
              <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: theme.text }}>
                Dashboard Variables
              </h2>
              <p style={{ margin: 0, fontSize: '11px', color: theme.textMuted, marginTop: '2px' }}>
                Use <code style={{ background: theme.cardHover, padding: '1px 4px', borderRadius: '3px' }}>$variableName</code> in panel queries to substitute selected values
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: theme.textMuted, cursor: 'pointer',
              padding: '6px', borderRadius: '6px',
              display: 'flex', alignItems: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body: two columns */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left: variable list */}
          <div style={{
            width: '200px',
            flexShrink: 0,
            borderRight: `1px solid ${theme.border}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
              {localVars.length === 0 && (
                <p style={{ color: theme.textMuted, fontSize: '12px', textAlign: 'center', padding: '16px 8px', margin: 0 }}>
                  No variables yet. Click "Add Variable" below.
                </p>
              )}
              {localVars.map(v => (
                <div
                  key={v.id}
                  onClick={() => { setSelectedId(v.id); setPreviewOptions([]); setNameError(''); }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    borderRadius: '7px',
                    marginBottom: '2px',
                    cursor: 'pointer',
                    background: selectedId === v.id ? `${theme.accent}20` : 'transparent',
                    border: selectedId === v.id ? `1px solid ${theme.accent}40` : '1px solid transparent'
                  }}
                  onMouseEnter={e => {
                    if (selectedId !== v.id) e.currentTarget.style.background = theme.cardHover;
                  }}
                  onMouseLeave={e => {
                    if (selectedId !== v.id) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.label || v.name || '(unnamed)'}
                    </div>
                    <div style={{ fontSize: '10px', color: theme.textMuted }}>
                      ${v.name || '?'} · {v.type}
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); handleDeleteVariable(v.id); }}
                    style={{
                      background: 'transparent', border: 'none',
                      color: theme.textMuted, cursor: 'pointer',
                      padding: '2px', borderRadius: '3px',
                      display: 'flex', alignItems: 'center', flexShrink: 0
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = theme.danger; }}
                    onMouseLeave={e => { e.currentTarget.style.color = theme.textMuted; }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div style={{ padding: '8px', flexShrink: 0, borderTop: `1px solid ${theme.border}` }}>
              <button
                onClick={handleAddVariable}
                style={{
                  width: '100%', padding: '8px',
                  background: theme.accent, border: 'none',
                  borderRadius: '7px', color: 'white',
                  fontSize: '12px', fontWeight: '600',
                  cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', gap: '4px'
                }}
              >
                <Plus size={13} />
                Add Variable
              </button>
            </div>
          </div>

          {/* Right: form */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {!selected ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <p style={{ color: theme.textMuted, fontSize: '13px' }}>
                  Select a variable from the list or add a new one.
                </p>
              </div>
            ) : (
              <>
                {/* Name */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>Variable Name *</label>
                  <input
                    value={selected.name}
                    onChange={e => updateSelected({ name: e.target.value })}
                    placeholder="e.g. machine_id"
                    style={{ ...inputStyle, borderColor: nameError ? theme.danger : theme.border }}
                  />
                  {nameError && (
                    <p style={{ margin: '4px 0 0', fontSize: '11px', color: theme.danger }}>{nameError}</p>
                  )}
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: theme.textMuted }}>
                    Use in queries as <code style={{ background: theme.cardHover, padding: '1px 4px', borderRadius: '3px' }}>${selected.name || 'variable_name'}</code>
                  </p>
                </div>

                {/* Label */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>Display Label</label>
                  <input
                    value={selected.label}
                    onChange={e => updateSelected({ label: e.target.value })}
                    placeholder="e.g. Machine"
                    style={inputStyle}
                  />
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: theme.textMuted }}>Shown in the filter bar above the dropdown.</p>
                </div>

                {/* Type */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>Type</label>
                  <select
                    value={selected.type}
                    onChange={e => updateSelected({ type: e.target.value, column: '', values: [], defaultValue: '' })}
                    style={inputStyle}
                  >
                    {VARIABLE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Query type fields */}
                {selected.type === 'query' && (
                  <>
                    {/* Engine picker */}
                    <div style={sectionStyle}>
                      <label style={labelStyle}>Database Engine</label>
                      <select
                        value={selected.dbEngine || 'questdb'}
                        onChange={e => updateSelected({ dbEngine: e.target.value, mysqlDatabase: '', table: '', column: '', defaultValue: '' })}
                        style={inputStyle}
                      >
                        <option value="questdb">QuestDB (agent)</option>
                        <option value="mysql">MySQL</option>
                      </select>
                    </div>

                    {/* MySQL database picker — only when engine=mysql */}
                    {selected.dbEngine === 'mysql' && (
                      <div style={sectionStyle}>
                        <label style={labelStyle}>MySQL Database</label>
                        <select
                          value={selected.mysqlDatabase || ''}
                          onChange={e => updateSelected({ mysqlDatabase: e.target.value, table: '', column: '', defaultValue: '' })}
                          style={inputStyle}
                        >
                          <option value="">— Select database —</option>
                          {mysqlDatabases.map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div style={sectionStyle}>
                      <label style={labelStyle}>Table</label>
                      <select
                        value={selected.table}
                        onChange={e => updateSelected({ table: e.target.value, column: '', defaultValue: '' })}
                        style={inputStyle}
                        disabled={selected.dbEngine === 'mysql' && !selected.mysqlDatabase}
                      >
                        <option value="">— Select table —</option>
                        {(selected.dbEngine === 'mysql' ? mysqlTables : (availableTables || [])).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div style={sectionStyle}>
                      <label style={labelStyle}>Column (DISTINCT values will be fetched)</label>
                      <select
                        value={selected.column}
                        onChange={e => updateSelected({ column: e.target.value, defaultValue: '' })}
                        style={inputStyle}
                        disabled={!selected.table}
                      >
                        <option value="">— Select column —</option>
                        {editingColumns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Custom type fields */}
                {selected.type === 'custom' && (
                  <div style={sectionStyle}>
                    <label style={labelStyle}>Values (comma-separated)</label>
                    <textarea
                      value={(selected.values || []).join(', ')}
                      onChange={e => updateSelected({
                        values: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                      })}
                      placeholder="PlantA, PlantB, PlantC"
                      rows={3}
                      style={{ ...inputStyle, height: 'auto', resize: 'vertical' }}
                    />
                  </div>
                )}

                {/* Slider (what-if) type fields */}
                {selected.type === 'slider' && (
                  <div style={sectionStyle}>
                    <label style={labelStyle}>Slider range</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                      <div>
                        <label style={{ fontSize: 10, color: theme.textMuted, display: 'block', marginBottom: 3 }}>Min</label>
                        <input
                          type="number"
                          value={selected.min ?? 0}
                          onChange={e => updateSelected({ min: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: theme.textMuted, display: 'block', marginBottom: 3 }}>Max</label>
                        <input
                          type="number"
                          value={selected.max ?? 100}
                          onChange={e => updateSelected({ max: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: 10, color: theme.textMuted, display: 'block', marginBottom: 3 }}>Step</label>
                        <input
                          type="number"
                          value={selected.step ?? 1}
                          onChange={e => updateSelected({ step: e.target.value })}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                    <label style={{ ...labelStyle, marginTop: 12 }}>Unit (optional)</label>
                    <input
                      value={selected.unit || ''}
                      onChange={e => updateSelected({ unit: e.target.value })}
                      placeholder="e.g. %, kg, °C"
                      style={inputStyle}
                    />
                  </div>
                )}

                {/* Default value */}
                <div style={sectionStyle}>
                  <label style={labelStyle}>Default Value</label>
                  {previewOptions.length > 0 ? (
                    <select
                      value={selected.defaultValue}
                      onChange={e => updateSelected({ defaultValue: e.target.value })}
                      style={inputStyle}
                    >
                      <option value="">None (show All)</option>
                      {previewOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={selected.defaultValue}
                      onChange={e => updateSelected({ defaultValue: e.target.value })}
                      placeholder="Leave blank for 'All'"
                      style={inputStyle}
                    />
                  )}
                </div>

                {/* Preview */}
                <div style={{ marginBottom: '16px' }}>
                  <button
                    onClick={handlePreview}
                    disabled={previewLoading || (selected.type === 'query' && (!selected.table || !selected.column))}
                    style={{
                      padding: '7px 14px',
                      background: 'transparent',
                      border: `1px solid ${theme.accent}`,
                      borderRadius: '6px',
                      color: theme.accent,
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      opacity: previewLoading ? 0.7 : 1
                    }}
                  >
                    <Play size={12} />
                    {previewLoading ? 'Loading...' : 'Preview Values'}
                  </button>
                  {previewOptions.length > 0 && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: theme.cardHover,
                      borderRadius: '6px',
                      border: `1px solid ${theme.border}`,
                      fontSize: '12px',
                      color: theme.textSecondary
                    }}>
                      <strong style={{ color: theme.text }}>{previewOptions.length} values found:</strong>{' '}
                      {previewOptions.slice(0, 15).join(', ')}
                      {previewOptions.length > 15 && ` … and ${previewOptions.length - 15} more`}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: 'transparent',
              border: `1px solid ${theme.border}`,
              borderRadius: '7px',
              color: theme.textSecondary,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '8px 20px',
              background: theme.accent,
              border: 'none',
              borderRadius: '7px',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Save Variables
          </button>
        </div>
      </div>
    </div>
  );
}
